import {
  PossibleDateSort,
  TableOverviewParams,
  TableOverviewResponse
} from "../types/table-overview";
import { BaseSettingsEndpoints } from "./base";

const TABLE_OVERVIEW_BASE_PATH = "/v1/dashboard";

export type TableOverviewBody = {
  date?: string;
  tz?: string;
  user_tz?: string;
  team_ids?: string[];
  limit?: number;
  cursor?: string;
  user_ids?: string[];
  date_sort_order?: PossibleDateSort;
  date_sort?: string;
  date_only?: boolean;
  office_ids?: string[];
  division_ids?: string[];
};

/**
 * The table overview filters, named as the API expects them. Empty filters are left out.
 */
export const buildTableOverviewBody = ({
  office_ids,
  division_ids,
  date,
  tz,
  user_tz,
  team_id,
  limit,
  cursor,
  user_id,
  date_sort_order,
  date_sort,
  is_single_date
}: TableOverviewParams): TableOverviewBody => {
  const body: TableOverviewBody = {};

  if (date) {
    body.date = date;
  }

  if (tz) {
    body.tz = tz;
  }

  if (user_tz) {
    body.user_tz = user_tz;
  }

  if (team_id && team_id.length > 0) {
    body.team_ids = team_id;
  }

  if (limit) {
    body.limit = limit;
  }

  if (cursor) {
    body.cursor = cursor;
  }

  if (user_id && user_id.length > 0) {
    body.user_ids = user_id;
  }

  if (date_sort) {
    body.date_sort_order = date_sort_order ?? PossibleDateSort.ASC;
    body.date_sort = date_sort;
  }

  if (is_single_date) body.date_only = true;

  if (office_ids && office_ids.length > 0) {
    body.office_ids = office_ids;
  }

  if (division_ids && division_ids.length > 0) {
    body.division_ids = division_ids;
  }

  return body;
};

// Well under the 8 KB most gateways allow for the request line
export const MAX_TABLE_OVERVIEW_URL_LENGTH = 4000;

// The GET names each id once per repeated parameter, so it keeps the singular names
const QUERY_PARAM_NAMES: Partial<Record<keyof TableOverviewBody, string>> = {
  team_ids: "team_id",
  user_ids: "user_id",
  office_ids: "office_id",
  division_ids: "division_id"
};

/**
 * The GET's query string for the same filters: arrays become repeated parameters, as the API
 * expects.
 */
export const buildTableOverviewQuery = (body: TableOverviewBody): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(body)) {
    if (Array.isArray(value)) {
      const name = QUERY_PARAM_NAMES[key as keyof TableOverviewBody] ?? key;
      for (const item of value) {
        params.append(name, item);
      }
    } else if (value !== undefined) {
      params.set(key, String(value));
    }
  }
  return params.toString();
};

export class TableOverviewClient extends BaseSettingsEndpoints {
  // The GET stays the default; long user and team selections overflow its URL (414), so those
  // go as a POST body instead
  async getTableOverview(params: TableOverviewParams) {
    const path = `${TABLE_OVERVIEW_BASE_PATH}/table_overview`;
    const body = buildTableOverviewBody(params);
    const queryString = buildTableOverviewQuery(body);
    const url = queryString ? `${path}?${queryString}` : path;

    if (url.length <= MAX_TABLE_OVERVIEW_URL_LENGTH) {
      return this.get<TableOverviewResponse>(url);
    }

    return this.post<TableOverviewBody, TableOverviewResponse>(path, body);
  }
}
